import {Component, EventEmitter, OnInit, Optional} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {NbWindowRef} from '@nebular/theme';
import {BaseComponent, FeatureValue, TemplateFeatureValuesMappingInfo} from 'compob2b-libs';
import {map, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-template-feature-value-mapper',
  templateUrl: './template-feature-value-mapper.component.html',
  styleUrls: ['./template-feature-value-mapper.component.css']
})
export class TemplateFeatureValueMapperComponent extends BaseComponent implements OnInit {
  sourceFeatureId: number;
  destFeatureId: number;
  form: FormGroup;
  selected = [];
  public updateMapping: EventEmitter<boolean>;

  valueMappingForm = new FormGroup({
    valueChooser: new FormControl(null),
    valueMappings: new FormArray([], this.valueMappingValidator)
  });

  featureValuesFrom: FeatureValue[];
  featureValuesTo: FeatureValue[];

  constructor(@Optional() protected readonly windowRef: NbWindowRef) {
    super();
  }

  public static createNewValueMapping(sourceValueId: number, destValueId: number): FormGroup {
    return new FormGroup({
      sourceValueId: new FormControl(sourceValueId, Validators.required),
      destValueId: new FormControl(destValueId, Validators.required),
    });
  }

  ngOnInit(): void {
    this.outerMapping.controls.forEach(control => {
      this.mapping.push(TemplateFeatureValueMapperComponent.createNewValueMapping(control.value.sourceValueId, control.value.destValueId));
    });
    this.syncSelected();
    this.cdr.detectChanges();

    this.compoApiService.get<TemplateFeatureValuesMappingInfo>(`/template/feature/${this.sourceFeatureId}/map-to/${this.destFeatureId}`)
      .pipe(
        map(response => response.data),
        takeUntil(this.unsubscribe),
      )
      .subscribe(values => {
        this.featureValuesFrom = this.compoApiService.naturalSort(values.featureValuesFrom, 'value');
        this.featureValuesTo = this.compoApiService.naturalSort(values.featureValuesTo, 'value');
        this.cdr.detectChanges();
      });
  }

  valueMappingValidator(): ValidatorFn {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (formArray instanceof FormArray) {

        const fromIDs = new Set<number>();
        const toIDs = new Set<number>();

        for (const control of formArray.controls) {
          const from: number = control.get('sourceValueId').value ? parseInt(control.get('sourceValueId').value, 10) : null;
          const to: number = control.get('destValueId').value ? parseInt(control.get('destValueId').value, 10) : null;

          if (from === null || fromIDs.has(from)) {
            return {valueUnique: true};
          }

          if (to === null || toIDs.has(to)) {
            return {valueUnique: true};
          }

          fromIDs.add(from);
          toIDs.add(to);
        }

        return null;
      }
      throw new Error('formArray is not an instance of FormArray');
    };
  }

  indexOf(id) {
    for (let index = 0; index < this.mapping.length; index++) {
      if (id == this.mapping.at(index).get('sourceValueId').value) {
        return index;
      }
    }

    return -1;
  }

  onFeatureValueChoose(featureValues: FeatureValue[]): void {
    for (let index = 0; index < featureValues.length; index++) {
      const value = featureValues[index];
      if (this.indexOf(value.id) < 0) {
        this.mapping.push(TemplateFeatureValueMapperComponent.createNewValueMapping(value.id, null));
      }
    }

    for (let index = 0; index < this.mapping.length; index++) {
      if (this.selected.indexOf(this.mapping.at(index).get('sourceValueId').value) < 0) {
        this.deleteValueMappingItem(index);
      }
    }

    this.cdr.detectChanges();
  }

  deleteValueMappingItem(index: number): void {
    this.mapping.removeAt(index);
    this.syncSelected();
    this.cdr.detectChanges();
  }

  private syncSelected() {
    this.selected = [];
    for (let index = 0; index < this.mapping.length; index++) {
      this.selected.push(this.mapping.at(index).get('sourceValueId').value);
    }
    this.selected.push();
    this.valueMappingForm.get('valueChooser').patchValue(this.selected);
  }

  get mapping(): FormArray {
    return this.valueMappingForm.get('valueMappings') as FormArray;
  }

  get outerMapping(): FormArray {
    return this.form.get('valueMappings') as FormArray;
  }

  saveValueMapping() {
    if (this.valueMappingForm.invalid) {
      return;
    }

    this.outerMapping.clear();
    this.mapping.controls.forEach(control => {
      this.outerMapping.push(TemplateFeatureValueMapperComponent.createNewValueMapping(control.value.sourceValueId, control.value.destValueId));
    });
    this.updateMapping.next(true);
    this.modalClose();
  }

  modalClose() {
    this.windowRef.close();
  }
}
